import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { authAPI, trainingGroupsAPI } from '../services/api';
import { useNotifications } from '../components/notifications';
import {
  UserGroupIcon,
  AcademicCapIcon,
  TrophyIcon,
  StarIcon,
  UsersIcon,
  ClockIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';

const GROUP_TYPE_ICONS = {
  novice: AcademicCapIcon,
  intermediate: UsersIcon,
  advanced: TrophyIcon,
  elite: StarIcon,
  masters: ClockIcon,
  junior: UserGroupIcon,
  development: AcademicCapIcon,
};

const GROUP_TYPE_COLORS = {
  novice: 'green',
  intermediate: 'blue',
  advanced: 'purple',
  elite: 'yellow',
  masters: 'gray',
  junior: 'pink',
  development: 'indigo',
};

const TrainingGroupApplication = ({ user, onSuccess, showPendingStatus = true }) => {
  const { showSuccess, showError } = useNotifications();
  const queryClient = useQueryClient();
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [selectedRole, setSelectedRole] = useState('rower');

  // Check if user has already applied (has requested_training_group_id)
  const hasApplied = user?.requested_training_group_id;

  // Fetch available training groups
  const { data: availableGroups = [], isLoading: isLoadingGroups } = useQuery(
    'availableTrainingGroups',
    () => trainingGroupsAPI.getAvailableGroupsForApplication(),
    {
      enabled: !hasApplied || !showPendingStatus,
    }
  );

  const applicationMutation = useMutation(
    (applicationData) => authAPI.submitTrainingGroupApplication(applicationData),
    {
      onSuccess: () => {
        showSuccess(
          `Application submitted successfully as ${selectedRole}! You will be notified when it is reviewed.`
        );
        queryClient.invalidateQueries('currentUser');
        queryClient.invalidateQueries('trainingGroups');
        // Reset form
        setSelectedGroupId('');
        setSelectedRole('rower');
        // Call parent success handler if provided
        if (onSuccess) {
          onSuccess();
        }
      },
      onError: (error) => {
        showError(error.response?.data?.detail || 'Failed to submit application');
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedGroupId) {
      showError('Please select a training group');
      return;
    }

    applicationMutation.mutate({
      requested_training_group_id: parseInt(selectedGroupId),
      requested_role: selectedRole,
    });
  };

  const getColorClasses = (color) => {
    const colorMap = {
      green: 'border-green-200 bg-green-50 text-green-900 hover:bg-green-100',
      blue: 'border-blue-200 bg-blue-50 text-blue-900 hover:bg-blue-100',
      purple: 'border-purple-200 bg-purple-50 text-purple-900 hover:bg-purple-100',
      yellow: 'border-yellow-200 bg-yellow-50 text-yellow-900 hover:bg-yellow-100',
      gray: 'border-gray-200 bg-gray-50 text-gray-900 hover:bg-gray-100',
      pink: 'border-pink-200 bg-pink-50 text-pink-900 hover:bg-pink-100',
      indigo: 'border-indigo-200 bg-indigo-50 text-indigo-900 hover:bg-indigo-100',
    };
    return colorMap[color] || colorMap.blue;
  };

  const getSelectedColorClasses = (color) => {
    const colorMap = {
      green: 'border-green-500 bg-green-100 ring-green-500',
      blue: 'border-blue-500 bg-blue-100 ring-blue-500',
      purple: 'border-purple-500 bg-purple-100 ring-purple-500',
      yellow: 'border-yellow-500 bg-yellow-100 ring-yellow-500',
      gray: 'border-gray-500 bg-gray-100 ring-gray-500',
      pink: 'border-pink-500 bg-pink-100 ring-pink-500',
      indigo: 'border-indigo-500 bg-indigo-100 ring-indigo-500',
    };
    return colorMap[color] || colorMap.blue;
  };

  // If user has already applied, show pending status only when showPendingStatus is true
  // This allows the form to be shown in modal for additional applications
  if (hasApplied && showPendingStatus) {
    return (
      <div className="mx-auto max-w-2xl">
        <div className="rounded-lg bg-white p-8 text-center shadow-sm">
          <div className="mb-6">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100">
              <ClockIcon className="h-8 w-8 text-yellow-600" />
            </div>
          </div>
          <h1 className="mb-4 text-3xl font-bold text-gray-900">Application Pending</h1>
          <p className="mb-6 text-lg text-gray-600">
            Your application to join a training group is currently being reviewed by our coaches.
          </p>
          <div className="rounded-lg bg-gray-50 p-6">
            <h3 className="mb-2 text-lg font-medium text-gray-900">What happens next?</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <p>• Our coaches will review your application within 1-2 days</p>
              <p>• You'll receive an email notification when you're added to a group</p>
              <p>• Once approved, you'll see your training group and can start participating</p>
            </div>
          </div>
          <p className="mt-6 text-sm text-gray-500">
            If you have questions, contact us at coach@rowingclub.com
          </p>
        </div>
      </div>
    );
  }

  if (isLoadingGroups) {
    return (
      <div className="mx-auto max-w-4xl">
        <div className="rounded-lg bg-white p-8 shadow-sm">
          <div className="flex items-center justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading available training groups...</span>
          </div>
        </div>
      </div>
    );
  }

  if (availableGroups.length === 0) {
    return (
      <div className="mx-auto max-w-2xl">
        <div className="rounded-lg bg-white p-8 text-center shadow-sm">
          <div className="mb-6">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
              <UserGroupIcon className="h-8 w-8 text-gray-600" />
            </div>
          </div>
          <h1 className="mb-4 text-3xl font-bold text-gray-900">No Available Groups</h1>
          <p className="mb-6 text-lg text-gray-600">
            There are currently no training groups available for application.
          </p>
          <p className="text-sm text-gray-500">
            Please check back later or contact a coach for assistance.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl">
      <div className="rounded-lg bg-white p-8 shadow-sm">
        <div className="mb-8 text-center">
          <UserGroupIcon className="mx-auto h-16 w-16 text-blue-600" />
          <h1 className="mt-4 text-3xl font-bold text-gray-900">
            {showPendingStatus ? 'Join a Training Group' : 'Apply to Additional Training Groups'}
          </h1>
          <p className="mt-2 text-lg text-gray-600">
            {showPendingStatus
              ? 'Welcome! Please select a training group to get started.'
              : 'Select an additional training group to apply to.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Training Group Selection */}
          <div>
            <h2 className="mb-4 text-xl font-semibold text-gray-900">Available Training Groups</h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {availableGroups.map((group) => {
                const Icon = GROUP_TYPE_ICONS[group.group_type] || UserGroupIcon;
                const color = GROUP_TYPE_COLORS[group.group_type] || 'blue';
                const isSelected = selectedGroupId === group.id.toString();
                const baseClasses =
                  'cursor-pointer rounded-lg border-2 p-4 transition-all duration-200';
                const colorClasses = isSelected
                  ? `${getSelectedColorClasses(color)} ring-2`
                  : getColorClasses(color);

                return (
                  <div
                    key={group.id}
                    className={`${baseClasses} ${colorClasses}`}
                    onClick={() => setSelectedGroupId(group.id.toString())}
                  >
                    <div className="mb-2 flex items-center">
                      <Icon className="mr-2 h-6 w-6" />
                      <h3 className="font-semibold">{group.name}</h3>
                    </div>
                    <div className="mb-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                          color === 'green'
                            ? 'bg-green-100 text-green-800'
                            : color === 'blue'
                              ? 'bg-blue-100 text-blue-800'
                              : color === 'purple'
                                ? 'bg-purple-100 text-purple-800'
                                : color === 'yellow'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : color === 'gray'
                                    ? 'bg-gray-100 text-gray-800'
                                    : color === 'pink'
                                      ? 'bg-pink-100 text-pink-800'
                                      : 'bg-indigo-100 text-indigo-800'
                        }`}
                      >
                        {group.group_type.replace('_', ' ')}
                      </span>
                    </div>
                    {group.description && (
                      <p className="mb-3 text-sm text-gray-600">{group.description}</p>
                    )}
                    <div className="flex items-center text-xs text-gray-500">
                      <UsersIcon className="mr-1 h-4 w-4" />
                      <span>{group.member_count} members</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Role Selection */}
          <div>
            <h2 className="mb-4 text-xl font-semibold text-gray-900">Role in Training Group</h2>
            <div className="space-y-3">
              <div className="flex items-center">
                <input
                  id="role_rower"
                  name="role"
                  type="radio"
                  value="rower"
                  checked={selectedRole === 'rower'}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="role_rower" className="ml-3 text-sm font-medium text-gray-700">
                  Rower
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="role_coach"
                  name="role"
                  type="radio"
                  value="coach"
                  checked={selectedRole === 'coach'}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="role_coach" className="ml-3 text-sm font-medium text-gray-700">
                  Coach
                </label>
              </div>
              <p className="text-xs text-gray-500">
                Select the role you would like to have in the training group. This will be reviewed
                by administrators.
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center">
            <button
              type="submit"
              disabled={applicationMutation.isLoading || !selectedGroupId}
              className="rounded-md bg-blue-600 px-8 py-3 font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {applicationMutation.isLoading
                ? 'Submitting...'
                : showPendingStatus
                  ? 'Join Training Group'
                  : 'Apply to Additional Group'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TrainingGroupApplication;
